import React, { isValidElement, PropsWithChildren, ReactNode } from 'react';
import {
  colors,
  NewTypography as Typography,
  spacing,
  Spinner,
  useViewport,
} from '@lamimed/pantheon';
import { NewTypographyProps } from '@lamimed/pantheon/dist/components/tokens/NewTypography';
import { StyledIcon } from '@styled-icons/styled-icon';
import { LucideIcon } from 'lucide-react';

import { Divider } from 'components/Divider';

import * as S from './styles';

export interface ContentBoxProps {
  padding?: string;
  marginBottom?: keyof typeof spacing;
  height?: string;
  horizontalAligment?: 'start' | 'center';
  verticalAlignment?: 'start' | 'center' | 'space-between';
  expanded?: boolean;
  loading?: boolean;
}

export interface TitleProps extends NewTypographyProps {
  icon?: LucideIcon | StyledIcon | ReactNode;
}

const ContentBox = ({
  children,
  loading = false,
  ...props
}: React.PropsWithChildren<ContentBoxProps>) => {
  const { greaterThan, lessThan } = useViewport();

  if (loading) {
    return (
      <S.ContentBoxWrapper
        height={
          greaterThan('tablet') && lessThan('desktop') ? '20rem' : '17.7rem'
        }
        expanded
        verticalAlignment="center"
        horizontalAligment="center"
        marginBottom="none"
      >
        <Spinner
          color="dark"
          positionX="center"
          positionY="center"
          size="medium"
        />
      </S.ContentBoxWrapper>
    );
  }

  return <S.ContentBoxWrapper {...props}>{children}</S.ContentBoxWrapper>;
};

const Title = ({
  children,
  icon,
  marginBottom,
  marginTop,
  ...props
}: React.PropsWithChildren<TitleProps>) => {
  const { greaterThan } = useViewport();

  return (
    <S.TitleWrapper marginBottom={marginBottom} marginTop={marginTop}>
      {icon && (
        <S.TitleIcon>
          {isValidElement(icon)
            ? icon
            : // TODO: check if we can remove this `as` solution deriving automatically the ReactNode outside the union.
              React.createElement(icon as LucideIcon | StyledIcon, {
                size: 18,
                color: colors.dark,
                strokeWidth: 1,
              })}
        </S.TitleIcon>
      )}

      <Typography
        as="h3"
        variant={greaterThan('tablet') ? 'heading_5' : 'heading_4'}
        marginBottom="none"
        lineHeight="none"
        {...props}
      >
        {children}
      </Typography>
    </S.TitleWrapper>
  );
};

export interface FooterProps extends PropsWithChildren {
  hasDivider?: boolean;
  alignItems?: 'flex-start' | 'flex-end' | 'center';
  dividerMarginTop?: keyof typeof spacing;
  dividerMarginBottom?: keyof typeof spacing;
}

const Footer = ({
  hasDivider = false,
  alignItems,
  dividerMarginTop = 'small',
  dividerMarginBottom = 'medium',
  children,
}: FooterProps) => {
  return (
    <S.FooterWrapper alignItems={alignItems} hasDivider={hasDivider}>
      {hasDivider && (
        <Divider
          marginTop={dividerMarginTop}
          marginBottom={dividerMarginBottom}
        />
      )}

      {children}
    </S.FooterWrapper>
  );
};

ContentBox.Title = Title;

ContentBox.Footer = Footer;

export default ContentBox;
